import { useId, useState, type ReactNode } from 'react';
import { CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';

interface InspectorSectionProps {
  title: string;
  icon?: ReactNode;
  /** A small trailing indicator (e.g. a status dot) shown next to the title, whether the section is open or collapsed. */
  badge?: ReactNode;
  defaultOpen?: boolean;
  /**
   * Controlled open state — pass together with `onOpenChange` when a caller
   * needs to force the section open from outside (e.g. a state elsewhere on
   * the page just became worth surfacing). Omit both to keep the section
   * uncontrolled, as every other caller does.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

/**
 * A titled, keyboard-toggleable collapsible section for the app editor's
 * inspector rail. One responsibility: show/hide `children` under `title`.
 */
export function InspectorSection({
  title,
  icon,
  badge,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className,
}: InspectorSectionProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const contentId = useId();

  return (
    <div className={cn('border-b border-border last:border-b-0', className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={contentId}
        className="flex w-full items-center gap-2 px-3 py-3 text-left text-sm font-medium text-foreground cursor-pointer bg-muted hover:bg-muted/70 transition-colors"
      >
        <CaretRightIcon
          size={14}
          className={cn(
            'text-muted-foreground shrink-0 transition-transform motion-reduce:transition-none',
            open && 'rotate-90',
          )}
        />
        {icon}
        <span className="flex-1">{title}</span>
        {badge}
      </button>
      {open && (
        <div id={contentId} className="px-3 pb-5 pt-0.5">
          {children}
        </div>
      )}
    </div>
  );
}
